import React from "react";
import { useTranslation } from "react-i18next";

import { Box, Checkbox, Chip, Typography } from "@mui/material";

import { getRemediationIcon } from "@/pages/browser-defender/demo/passwordMisuseUpdated/components/PasswordMisuseRemediationIcons";
import { PasswordMisuseURLAlert } from "@/pages/browser-defender/demo/passwordMisuseUpdated/service/domain";

import PasswordMisuseAlertTypeChip from "./PasswordMisuseAlertTypeChip";

type AlertRowProps = {
  alert: PasswordMisuseURLAlert;
  selected?: boolean;
  onSelect?: (alertId: number, selected: boolean) => void;
};

const UserActionChip = ({ action }: { action: string }) => {
  const { t } = useTranslation();

  const getLabel = () => {
    switch (action) {
      case "mark_as_safe":
        return t("Domain trusted");
      case "ignore_once":
      case null:
        return t("Alert closed");
      default:
        return t(action);
    }
  };

  return (
    <Chip
      label={getLabel()}
      size="small"
      sx={{
        backgroundColor: "rgba(88, 101, 242, 0.08)",
        color: "#5865F2",
        fontWeight: "normal",
        borderRadius: "16px",
        padding: "0 8px",
        fontSize: "0.75rem",
        border: "1px solid #5865F2",
      }}
    />
  );
};

const PasswordMisuseAlertRow = ({
  alert,
  selected = false,
  onSelect = () => {},
}: AlertRowProps) => {
  const { t } = useTranslation();

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelect(alert.id, e.target.checked);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        borderBottom: "1px solid #f0f0f0",
        backgroundColor: selected ? "rgba(224, 225, 255, 0.3)" : "transparent",
        "&:hover": {
          backgroundColor: "rgba(224, 225, 255, 0.1)",
        },
      }}
    >
      <Box sx={{ flex: 2, display: "flex", alignItems: "center" }}>
        <Checkbox
          size="small"
          onChange={handleCheckboxChange}
          checked={selected}
          sx={{ mr: 1 }}
        />
        <Box sx={{ color: "#1976D2" }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {alert.url}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {alert.userEmail} ({alert.timestamp.toLocaleString()})
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 0.7, display: "flex", justifyContent: "center", mr: 1 }}>
        <PasswordMisuseAlertTypeChip alertType={alert.type} />
      </Box>

      <Box sx={{ flex: 0.7, display: "flex", justifyContent: "center" }}>
        <UserActionChip action={alert.userAction} />
      </Box>

      <Box
        sx={{
          flex: 0.5,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {getRemediationIcon(t, alert.remediation)}
      </Box>
    </Box>
  );
};

export default PasswordMisuseAlertRow;
